import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Shield, User, Edit2, X } from 'lucide-react';
import { api } from '../api';
import './UserManagement.css';

// Module-level cache so switching tabs doesn't refetch
let usersCache = null;
let usersCacheTimestamp = 0;
const CACHE_TTL_MS = 60_000; // 1 minute

export default function UserManagement({ currentUser, showAddUser: externalShowAddUser, onAddUserComplete }) {
  // Initialize from cache for instant paint if available
  const [users, setUsers] = useState(() => usersCache || []);
  const [showAddUser, setShowAddUser] = useState(false);
  // Only show loading if we don't have cached data
  const [loading, setLoading] = useState(() => !usersCache);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: 'user',
    password: ''
  });

  const loadUsers = async ({ force = false } = {}) => {
    // Skip if cache is fresh and not forced
    const cacheAge = Date.now() - usersCacheTimestamp;
    if (!force && usersCache && cacheAge < CACHE_TTL_MS) {
      setUsers(usersCache);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await api.getUsers();
      const list = response.users || [];
      setUsers(list);
      usersCache = list;
      usersCacheTimestamp = Date.now();
      setError('');
    } catch (err) {
      setError('Failed to load users');
      console.error('Load users error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (externalShowAddUser) {
      setShowAddUser(true);
    }
  }, [externalShowAddUser]);

  // Only show for admin users
  if (!currentUser || currentUser.role !== 'admin') {
    return null;
  }

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newUser.name || !newUser.email || !newUser.password) {
      setError('All fields are required');
      return;
    }

    try {
      setLoading(true);
      const response = await api.addUser(newUser);
      const updated = [response.user, ...users];
      setUsers(updated);
      usersCache = updated;
      usersCacheTimestamp = Date.now();
      setSuccess('User added successfully!');
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      setShowAddUser(false);
      if (onAddUserComplete) {
        onAddUserComplete(response.user);
      }
    } catch (err) {
      setError(err.message || 'Failed to add user');
      console.error('Add user error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUserClick = () => {
    setShowAddUser(true);
  };

  const handleEditUserClick = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      password: ''
    });
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setLoading(true);
      const updateData = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role
      };
      if (editForm.password && editForm.password.length >= 6) {
        updateData.password = editForm.password;
      }
      
      const response = await api.updateUser(editingUser.id, updateData);
      
      // Update users list
      const updated = users.map(u => u.id === editingUser.id ? response.user : u);
      setUsers(updated);
      usersCache = updated;
      usersCacheTimestamp = Date.now();
      setSuccess('User updated successfully!');
      setEditingUser(null);
    } catch (err) {
      setError(err.message || 'Failed to update user');
      console.error('Update user error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    return role === 'admin' ? Shield : User;
  };

  const getRoleColor = (role) => {
    return role === 'admin' ? 'admin' : 'user';
  };

  return (
    <section className="user-management fade-in">
      <div className="user-management-header">
        <div className="user-management-title">
          <Users size={20} />
          <h3>User Management</h3>
        </div>
        <button
          type="button"
          className="add-user-btn"
          onClick={handleAddUserClick}
          disabled={loading}
        >
          <UserPlus size={16} />
          Add User
        </button>
      </div>

      {error && <div className="user-management-error">{error}</div>}
      {success && <div className="user-management-success">{success}</div>}

      {showAddUser && (
        <div className="add-user-modal">
          <div className="add-user-content">
            <h4>Add New User</h4>
            <form onSubmit={handleAddUser} className="add-user-form">
              <div className="form-group">
                <label htmlFor="userName">Name</label>
                <input
                  id="userName"
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Enter user name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="userEmail">Email</label>
                <input
                  id="userEmail"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="userPassword">Password</label>
                <input
                  id="userPassword"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Enter password"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="userRole">Role</label>
                <select
                  id="userRole"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowAddUser(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="add-user-modal">
          <div className="add-user-content">
            <h4>Edit User</h4>
            <form onSubmit={handleEditUser} className="add-user-form">
              <div className="form-group">
                <label htmlFor="editName">Name</label>
                <input
                  id="editName"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Enter user name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="editEmail">Email</label>
                <input
                  id="editEmail"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="editRole">Role</label>
                <select
                  id="editRole"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  disabled={editingUser.id === currentUser.id}
                  title={editingUser.id === currentUser.id ? "Cannot change your own role. Ask another admin." : ""}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
                {editingUser.id === currentUser.id && (
                  <p className="field-help">You cannot change your own role</p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="editPassword">
                  New Password <span className="optional">(leave blank to keep current)</span>
                </label>
                <input
                  id="editPassword"
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Enter new password (min 10 chars, letter + number)"
                  minLength={10}
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setEditingUser(null)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="users-list">
        {loading && users.length === 0 ? (
          <div className="users-grid">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="user-card user-skeleton">
                <div className="skeleton-shimmer skeleton-avatar-round" />
                <div className="user-info">
                  <div className="skeleton-shimmer skeleton-line-70" />
                  <div className="skeleton-shimmer skeleton-line-50" />
                  <div className="skeleton-shimmer skeleton-badge" />
                </div>
                <div className="skeleton-shimmer skeleton-btn-sq" />
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="no-users">No users found</div>
        ) : (
          <div className="users-grid">
            {users.map((user) => {
              const RoleIcon = getRoleIcon(user.role);
              const isCurrentUser = user.id === currentUser.id;
              return (
                <div key={user.id} className={`user-card user-${getRoleColor(user.role)}`}>
                  <div className="user-avatar">
                    <RoleIcon size={20} />
                  </div>
                  <div className="user-info">
                    <h4>{user.name}</h4>
                    <p>{user.email}</p>
                    <span className={`user-role-badge role-${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </div>
                  <button
                    className="edit-user-btn"
                    onClick={() => handleEditUserClick(user)}
                    disabled={loading}
                    title={isCurrentUser ? "Edit your profile (role change requires another admin)" : "Edit user"}
                  >
                    <Edit2 size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
